import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { useEffect, useRef, useState } from 'react';
import { paymentServiceApi } from '@/services/api';
import { CheckCircle2, ChevronRight, Percent, XCircle } from 'lucide-react';
import { readClipboardText } from '@/utils/clipboard';
import { Capacitor } from '@capacitor/core';
import { AppLauncher } from '@capacitor/app-launcher';
import { toPersianNumber } from '@/utils/persianNumber';
import { getStoreProvider, isNativeStoreBuild, onStorePurchaseStarted, purchaseFromStore, restoreAndVerifyStorePurchases, verifyStorePurchase, type StorePaymentIntent } from '@/services/inAppBilling';
import { analyticsService } from '@/services/analyticsService';
import { adtraceService } from '@/services/adtraceService';
import { getNotificationAttribution } from '@/services/notificationAttribution';

interface PaymentConfirmationProps {
  className?: string;
}

interface SubscriptionPlan {
  plan_name: string;
  discount_price: string | null;
  gems_amount?: number;
  duration_unit: 'month' | 'day' | 'year';
  duration_value: number;
  price: string;
  plan_id: number;
}

interface DiscountValidationResponse {
  valid: boolean;
  code?: string | null;
  target_type: 'subscription' | 'gems';
  target_id: number;
  plan_name?: string | null;
  original_amount: string;
  discount_percent: number;
  discount_amount: string;
  final_amount: string;
  vat_amount: string;
  total_amount: string;
  message: string;
}

interface PriceSummary {
  originalAmount: string;
  discountPercent: number;
  discountAmount: string;
  finalAmount: string;
  vatAmount: string;
  totalAmount: string;
}

interface PaymentResult {
  success: boolean;
  code?: number | null;
  message?: string | null;
  order_id?: string | null;
  status?: string | null;
  plan_name?: string | null;
  amount?: string | null;
}

const isThreeMonthSubscriptionPlan = (
  plan: SubscriptionPlan,
  requestedPlan?: {
    duration_unit?: string;
    duration_value?: number;
    durationUnit?: string;
    durationValue?: number;
  }
) => {
  const requestedUnit = requestedPlan?.duration_unit || requestedPlan?.durationUnit;
  const requestedValue = Number(requestedPlan?.duration_value || requestedPlan?.durationValue || 0);

  if (
    requestedUnit &&
    requestedValue &&
    plan.duration_unit === requestedUnit &&
    Number(plan.duration_value) === requestedValue
  ) {
    return true;
  }

  if (plan.duration_unit === 'month' && Number(plan.duration_value) === 3) {
    return true;
  }

  if (plan.duration_unit === 'day' && Number(plan.duration_value) >= 89 && Number(plan.duration_value) <= 93) {
    return true;
  }

  return /سه|3|three/i.test(plan.plan_name) && /ماه|month/i.test(plan.plan_name);
};

const getProductRevenueEventToken = (
  paymentType: string,
  plan: SubscriptionPlan | null,
  planData?: { gems_amount?: number },
): string => {
  if (paymentType === 'gems') {
    const gemsAmount = Number(planData?.gems_amount ?? plan?.gems_amount ?? 0);
    if (gemsAmount === 100) return '4pk8nd';
    if (gemsAmount === 200) return 'zoc0z3';
    if (gemsAmount === 400) return 'om6kce';
    return 'szs0yw';
  }

  const durationValue = Number(plan?.duration_value || 0);
  if (
    (plan?.duration_unit === 'month' && durationValue === 1)
    || (plan?.duration_unit === 'day' && durationValue >= 27 && durationValue <= 31)
  ) return 'v2dham';
  if (
    (plan?.duration_unit === 'month' && durationValue === 3)
    || (plan?.duration_unit === 'day' && durationValue >= 89 && durationValue <= 93)
  ) return 'barr9m';
  if (
    (plan?.duration_unit === 'month' && durationValue === 6)
    || (plan?.duration_unit === 'day' && durationValue >= 178 && durationValue <= 186)
  ) return '440hx6';
  return 'hw3lst';
};

const getNativePaymentErrorCode = (message: string): string | null => {
  const match = message.match(/(?:response(?:\s+code)?|code)\s*[-_: ]*\s*(-?\d{3,})/i)
    || message.match(/\b(-?(?:1002|1005))\b/);
  return match?.[1] || null;
};

const isNativePaymentCancellation = (message: string): boolean => {
  const code = getNativePaymentErrorCode(message);
  return code === '1005' || code === '-1005' || /user\s*(cancelled|canceled)|user\s*cancel|cancelled\s*by\s*user|operation\s*(cancelled|canceled)|purchase\s*(cancelled|canceled)|dismissed|abort|لغو|انصراف/i.test(message);
};

// Bazaar's billing SDK can report a dismissed purchase sheet as BAD_RESPONSE
// (-1002) instead of USER_CANCELLED (-1005). It is only safe to treat this as
// cancellation when no purchase was returned to the app.
const isStorePaymentCancellation = (
  message: string,
  provider?: string | null,
  hasPurchase = false,
): boolean => {
  if (isNativePaymentCancellation(message)) return true;
  const code = getNativePaymentErrorCode(message);
  return provider === 'bazaar' && !hasPurchase && (code === '1002' || code === '-1002');
};

const getStoreDisplayName = (provider?: string | null): string => provider === 'bazaar' ? 'بازار' : 'مایکت';

const getNativePaymentErrorMessage = (message: string, provider?: string | null): string => {
  if (/network|timeout|connection|internet|قطع|شبکه/i.test(message)) {
    return 'ارتباط با درگاه پرداخت برقرار نشد. اتصال اینترنت را بررسی کنید و دوباره تلاش کنید.';
  }
  if (/already|owned|خریداری/i.test(message)) {
    return 'این محصول قبلاً خریداری شده یا خرید قبلی هنوز تکمیل نشده است.';
  }
  return `پرداخت در ${getStoreDisplayName(provider)} ناموفق بود. در صورت کسر وجه، مبلغ پس از بررسی به حساب شما بازمی‌گردد.`;
};

const PaymentConfirmation = ({ className }: PaymentConfirmationProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  // The callback route has been consumed. Do not reopen an old payment result
  // on the next cold start.
  useEffect(() => {
    try {
      window.localStorage.removeItem('booka_pending_payment_route');
    } catch {
      // Ignore storage failures; the current route is already available.
    }
  }, []);
  const {
    planId,
    paymentType,
    planData,
    autoSelectSubscriptionPlan,
    autoDiscountCode,
  } = location.state || {};
  const notificationAttribution = getNotificationAttribution();
  const analyticsSource = location.state?.source === 'notification' || notificationAttribution
    ? 'notification'
    : undefined;
  const notificationCampaign = location.state?.notificationCampaign || notificationAttribution?.campaign;

  const formatPrice = (value: string | null | undefined): string => {
    if (!value) return '';
    const num = Number.parseFloat(value);
    if (Number.isNaN(num)) return value;
    return `${num.toLocaleString('fa-IR')} تومان`;
  };

  const buildDefaultPriceSummary = (plan: SubscriptionPlan | null): PriceSummary | null => {
    if (!plan) return null;
    const effectiveBase = plan.discount_price || plan.price;
    const effectiveBaseNum = Number.parseFloat(effectiveBase || '0');
    const vatAmount = effectiveBaseNum * 0.10;
    const totalAmount = effectiveBaseNum + vatAmount;

    return {
      originalAmount: effectiveBase,
      discountPercent: 0,
      discountAmount: '0',
      finalAmount: effectiveBase,
      vatAmount: String(vatAmount),
      totalAmount: String(totalAmount),
    };
  };

  const [data, setData] = useState<SubscriptionPlan | null>(null);
  const [resolvedPlanId, setResolvedPlanId] = useState<number | null>(planId || null);
  const [resolvedPaymentType, setResolvedPaymentType] = useState<string>(
    paymentType || (autoSelectSubscriptionPlan ? 'subscription' : '')
  );
  const [priceSummary, setPriceSummary] = useState<PriceSummary | null>(null);
  const [discountCodeInput, setDiscountCodeInput] = useState('');
  const [appliedDiscountCode, setAppliedDiscountCode] = useState<string | null>(null);
  const [discountFeedback, setDiscountFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyDone, setVerifyDone] = useState(false);
  const [verifyResult, setVerifyResult] = useState<PaymentResult | null>(null);
  const [nativePurchaseStarted, setNativePurchaseStarted] = useState(false);
  const [nativeStoreProvider, setNativeStoreProvider] = useState<string | null>(null);
  const inFlightVerify = useRef(false);
  const verifyRetryCount = useRef(0);
  const autoDiscountAppliedRef = useRef(false);
  const verifyStartAt = useRef(0);
  const trackedPaymentStatusesRef = useRef(new Set<string>());
  const trackedAdtraceRevenueRef = useRef(new Set<string>());
  const MIN_SPINNER_MS = 800;
  const MAX_VERIFY_RETRIES = 10;
  const VERIFY_RETRY_DELAY_MS = 2000;

  const trackPaymentStatus = (
    status: 'completed' | 'failed' | 'cancelled',
    details?: { orderId?: string | null; provider?: string | null },
  ) => {
    const orderId = details?.orderId || null;
    const trackingKey = `${orderId || 'unknown'}:${status}`;
    if (trackedPaymentStatusesRef.current.has(trackingKey)) return;
    trackedPaymentStatusesRef.current.add(trackingKey);

    void analyticsService.logEvent('payment_status', {
      status,
      plan_id: resolvedPlanId ? String(resolvedPlanId) : 'unknown',
      plan_name: data?.plan_name || 'unknown',
      payment_type: resolvedPaymentType || 'unknown',
      ...((details?.provider || nativeStoreProvider)
        ? { store_provider: details?.provider || nativeStoreProvider }
        : {}),
      ...(orderId ? { transaction_id: orderId } : {}),
      ...(analyticsSource
        ? {
          source: analyticsSource,
          notification_campaign: notificationCampaign || 'unknown',
        }
        : {}),
    });
  };

  useEffect(() => {
    if (!isNativeStoreBuild()) return;

    let subscription: { remove: () => Promise<void> } | null = null;
    let active = true;
    void onStorePurchaseStarted(() => {
      if (active) setNativePurchaseStarted(true);
    }).then((handle) => {
      if (active) subscription = handle;
      else void handle.remove();
    }).catch((error) => {
      console.warn('Failed to subscribe to native purchase events', error);
    });

    return () => {
      active = false;
      if (subscription) void subscription.remove();
    };
  }, []);

  useEffect(() => {
    if (!planId || !planData) return;

    const nextData: SubscriptionPlan = {
      plan_name: planData.plan_name,
      price: planData.price,
      discount_price: planData.discount_price,
      duration_unit: planData.duration_unit || 'day',
      duration_value: planData.duration_value || 0,
      gems_amount: planData.gems_amount,
      plan_id: planId,
    };

    setData(nextData);
    setResolvedPlanId(planId);
    setResolvedPaymentType(paymentType || 'subscription');
    setPriceSummary(buildDefaultPriceSummary(nextData));
  }, [planId, planData]);

  useEffect(() => {
    if (!autoSelectSubscriptionPlan || planId || planData) return;

    let isMounted = true;

    const loadAutoSelectedSubscriptionPlan = async () => {
      try {
        const res = await paymentServiceApi.get<SubscriptionPlan[]>('/subscription-plans/');
        if (!res.success || !Array.isArray(res.data)) {
          throw new Error(res.error || 'Failed to load subscription plans');
        }

        const selectedPlan = res.data.find((plan) => (
          isThreeMonthSubscriptionPlan(plan, autoSelectSubscriptionPlan)
        ));

        if (!selectedPlan) {
          throw new Error('Requested subscription plan was not found');
        }

        if (!isMounted) return;

        setData(selectedPlan);
        setResolvedPlanId(selectedPlan.plan_id);
        setResolvedPaymentType('subscription');
        setPriceSummary(buildDefaultPriceSummary(selectedPlan));
      } catch (error) {
        if (!isMounted) return;

        console.error('Failed to auto-select subscription plan from notification:', error);
        setDiscountFeedback({
          type: 'error',
          message: error instanceof Error ? error.message : 'خطا در دریافت پلن اشتراک',
        });
      }
    };

    loadAutoSelectedSubscriptionPlan();

    return () => {
      isMounted = false;
    };
  }, [autoSelectSubscriptionPlan, planId, planData]);

  const handleGoBack = () => navigate(-1);

  const resetDiscountState = () => {
    setAppliedDiscountCode(null);
    setDiscountFeedback(null);
    setPriceSummary(buildDefaultPriceSummary(data));
  };

  const handlePasteDiscountCode = async () => {
    try {
      const pastedText = await readClipboardText();
      if (!pastedText) return;

      const nextValue = pastedText.trim().toUpperCase();
      setDiscountCodeInput(nextValue);
      if (appliedDiscountCode && nextValue !== appliedDiscountCode) {
        setAppliedDiscountCode(null);
        setPriceSummary(buildDefaultPriceSummary(data));
        setDiscountFeedback(null);
      }
    } catch (error) {
      console.warn('Failed to paste discount code', error);
    }
  };

  const handleDiscountCodeInputChange = (value: string) => {
    setDiscountCodeInput(value.toUpperCase());
  };

  const applyDiscountCode = async (code: string) => {
    if (!data || !resolvedPlanId || !resolvedPaymentType) return;

    const normalizedCode = code.trim().toUpperCase();
    if (!normalizedCode) {
      resetDiscountState();
      setDiscountFeedback({
        type: 'error',
        message: 'لطفاً کد تخفیف را وارد کنید.',
      });
      return;
    }

    try {
      setIsApplyingDiscount(true);
      setDiscountFeedback(null);

      const res = await paymentServiceApi.post<DiscountValidationResponse>('/discount-codes/validate', {
        code: normalizedCode,
        target_type: resolvedPaymentType,
        target_id: resolvedPlanId,
      });

      if (!res.success || !res.data) {
        resetDiscountState();
        setDiscountFeedback({
          type: 'error',
          message: res.error || res.detail || 'اعتبارسنجی کد تخفیف ناموفق بود',
        });
        return;
      }

      if (!res.data.valid) {
        resetDiscountState();
        setDiscountFeedback({
          type: 'error',
          message: res.data.message,
        });
        return;
      }

      const nextCode = res.data.code || normalizedCode;
      setAppliedDiscountCode(nextCode);
      setDiscountCodeInput(nextCode);
      setPriceSummary({
        originalAmount: res.data.original_amount,
        discountPercent: res.data.discount_percent,
        discountAmount: res.data.discount_amount,
        finalAmount: res.data.final_amount,
        vatAmount: res.data.vat_amount,
        totalAmount: res.data.total_amount,
      });
      setDiscountFeedback({
        type: 'success',
        message: res.data.message,
      });
    } catch (e: unknown) {
      resetDiscountState();
      setDiscountFeedback({
        type: 'error',
        message: e instanceof Error ? e.message : 'خطا در بررسی کد تخفیف',
      });
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  const handleApplyDiscount = async () => {
    await applyDiscountCode(discountCodeInput);
  };

  useEffect(() => {
    if (!autoDiscountCode || !data || !resolvedPlanId || !resolvedPaymentType || autoDiscountAppliedRef.current) {
      return;
    }

    const normalizedCode = String(autoDiscountCode).trim().toUpperCase();
    if (!normalizedCode) {
      return;
    }

    autoDiscountAppliedRef.current = true;
    setDiscountCodeInput(normalizedCode);
    void applyDiscountCode(normalizedCode);
  }, [autoDiscountCode, data, resolvedPlanId, resolvedPaymentType]);

  const createAndRedirect = async () => {
    if (!data) return;

    let nativeIntent: StorePaymentIntent | null = null;
    let nativePurchase: { orderId?: string; provider?: string } | null = null;
    let nativeProvider: string | null = null;
    try {
      setIsCreating(true);
      if (isNativeStoreBuild()) {
        nativeProvider = await getStoreProvider();
        setNativeStoreProvider(nativeProvider);
        if (!resolvedPlanId || (resolvedPaymentType !== 'subscription' && resolvedPaymentType !== 'gems')) {
          throw new Error('اطلاعات پلن پرداخت معتبر نیست');
        }
        setVerifyDone(false);
        setVerifyResult(null);
        setNativePurchaseStarted(false);
        const { purchase, intent } = await purchaseFromStore(
          resolvedPaymentType,
          resolvedPlanId,
          appliedDiscountCode,
        );
        nativeIntent = intent;
        nativePurchase = purchase;
        setIsVerifying(true);
        const verified = await verifyStorePurchase(resolvedPaymentType, resolvedPlanId, purchase, intent.order_id);
        setVerifyResult({
          success: verified?.success === true,
          message: verified?.message,
          order_id: verified?.order_id,
          status: verified?.status,
          plan_name: verified?.plan_name,
          amount: verified?.amount,
        });
        try { sessionStorage.removeItem('booka_myket_pending_intent'); sessionStorage.removeItem('booka_bazaar_pending_intent'); } catch {}
        setVerifyDone(true);
        return;
      }
      const res = await paymentServiceApi.post<any>('/payments/create', {
        plan_id: resolvedPlanId,
        payment_type: resolvedPaymentType || 'subscription',
        discount_code: appliedDiscountCode || undefined,
        callback_mode: Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android' ? 'app' : 'web',
      });

      if (!res.success || !res.data?.success || !res.data?.start_pay_url) {
        throw new Error(res.data?.message || res.error || 'خطا در ایجاد تراکنش');
      }

      try {
        sessionStorage.setItem('payment_order_id', res.data.order_id || '');
        sessionStorage.setItem('payment_plan_id', String(resolvedPlanId || ''));
      } catch {}

      if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
        // Keep the gateway outside the WebView. The backend receives the app
        // callback mode and redirects to booka://payment after the gateway callback.
        await AppLauncher.openUrl({ url: res.data.start_pay_url });
      } else {
        // Preserve the existing website flow.
        window.location.href = res.data.start_pay_url;
      }
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'خطا در ایجاد پرداخت';
      if (isNativeStoreBuild()) {
        // The store keeps a successful but unconsumed purchase and may return
        // "already owned" on the next attempt. Recover and verify it instead
        // of showing a new failure page.
        if (/already|owned|قبلاً|قبلا|خریداری/.test(message)) {
          try {
            const restored = await restoreAndVerifyStorePurchases();
            const recovered = restored.results.find((result: any) => result?.success);
            if (recovered) {
              setVerifyResult(recovered);
              setVerifyDone(true);
              return;
            }
          } catch (restoreError) {
            console.warn('Failed to recover an already-owned store purchase', restoreError);
          }
        }
        if (isStorePaymentCancellation(message, nativeProvider, Boolean(nativePurchase))) {
          let pendingOrderId = nativeIntent?.order_id;
          if (!pendingOrderId) {
            try {
              const pending = JSON.parse(sessionStorage.getItem(`booka_${nativeProvider || 'myket'}_pending_intent`) || 'null');
              pendingOrderId = pending?.order_id;
            } catch {}
          }
          if (pendingOrderId) {
            try {
              const provider = nativePurchase?.provider || nativeProvider || 'myket';
              await paymentServiceApi.post(`/payments/${provider}/cancel`, { order_id: pendingOrderId });
            } catch (cancelError) {
              console.warn('Failed to mark store intent as cancelled', cancelError);
            }
          }
          trackPaymentStatus('cancelled', {
            orderId: pendingOrderId,
            provider: nativeProvider,
          });
          try { sessionStorage.removeItem('booka_myket_pending_intent'); sessionStorage.removeItem('booka_bazaar_pending_intent'); } catch {}
          // Keep the payment details page open so the user can try again.
          setVerifyDone(false);
          setVerifyResult(null);
          setNativePurchaseStarted(false);
          return;
        }
        const serverPaymentResult = (e as { paymentResult?: PaymentResult })?.paymentResult;
        if (nativePurchase && serverPaymentResult) {
          try { sessionStorage.removeItem('booka_myket_pending_intent'); sessionStorage.removeItem('booka_bazaar_pending_intent'); } catch {}
          setVerifyResult({
            success: false,
            code: serverPaymentResult.code ?? null,
            order_id: serverPaymentResult.order_id || nativeIntent?.order_id || nativePurchase.orderId || null,
            plan_name: serverPaymentResult.plan_name || nativeIntent?.plan_name || data.plan_name,
            amount: serverPaymentResult.amount || nativeIntent?.amount || data.price,
            status: serverPaymentResult.status || 'failed',
            message: serverPaymentResult.message || 'تأیید پرداخت ناموفق بود',
          });
          setVerifyDone(true);
          return;
        }
        if (!nativePurchase) {
          let pendingOrderId = nativeIntent?.order_id;
          try {
              const pending = JSON.parse(sessionStorage.getItem(`booka_${nativeProvider || 'myket'}_pending_intent`) || 'null');
            pendingOrderId = pendingOrderId || pending?.order_id;
          } catch {}
          if (pendingOrderId) {
            try {
              const provider = nativePurchase?.provider || nativeProvider || 'myket';
              await paymentServiceApi.post(`/payments/${provider}/fail`, {
                order_id: pendingOrderId,
                error_code: getNativePaymentErrorCode(message) || `${String(provider).toUpperCase()}_PAYMENT_FAILED`,
                message,
              });
            } catch (failError) {
              console.warn('Failed to mark store intent as failed', failError);
            }
          }
        }
        setVerifyResult({
          success: false,
          code: getNativePaymentErrorCode(message) || (nativePurchase?.orderId ? 'STORE_VERIFY_FAILED' : 'STORE_PAYMENT_FAILED'),
          order_id: nativeIntent?.order_id || nativePurchase?.orderId || null,
          plan_name: nativeIntent?.plan_name || data.plan_name,
          amount: nativeIntent?.amount || data.price,
          status: 'failed',
          message: getNativePaymentErrorMessage(message, nativePurchase?.provider || nativeProvider),
        });
        setVerifyDone(true);
      } else {
        alert(message);
      }
    } finally {
      setIsVerifying(false);
      setIsCreating(false);
    }
  };

  useEffect(() => {
    if (!verifyResult) return;

    const status = verifyResult.success
      ? 'completed'
      : verifyResult.status === 'cancelled'
        ? 'cancelled'
        : 'failed';

    trackPaymentStatus(status, {
      orderId: verifyResult.order_id,
      provider: nativeStoreProvider,
    });

    if (!verifyResult.success || !verifyResult.amount) return;

    const orderId = verifyResult.order_id || null;
    const revenueKey = `${orderId || 'unknown'}:${resolvedPaymentType}:${resolvedPlanId || 'unknown'}`;
    if (trackedAdtraceRevenueRef.current.has(revenueKey)) return;

    const amountInToman = Number(verifyResult.amount);
    if (!Number.isFinite(amountInToman) || amountInToman <= 0) return;

    trackedAdtraceRevenueRef.current.add(revenueKey);
    const eventToken = getProductRevenueEventToken(resolvedPaymentType, data, planData);
    const parameters: Record<string, string | number | boolean> = {
      plan_id: resolvedPlanId ? String(resolvedPlanId) : 'unknown',
      plan_name: data?.plan_name || 'unknown',
      payment_type: resolvedPaymentType || 'unknown',
      store_provider: nativeStoreProvider || 'gateway',
      product_id: planData?.product_id || data?.plan_name || 'unknown',
      ...(orderId ? { transaction_id: orderId } : {}),
      ...(appliedDiscountCode ? { discount_code: appliedDiscountCode } : {}),
      ...(resolvedPaymentType === 'gems' && planData?.gems_amount
        ? { gems_amount: planData.gems_amount }
        : {}),
    };

    void adtraceService.trackRevenue({
      eventToken,
      // Payment-service amounts and the UI are in Toman; IRR is Rial.
      revenue: amountInToman * 10,
      currency: 'IRR',
      orderId,
      parameters,
    }).catch((error) => {
      trackedAdtraceRevenueRef.current.delete(revenueKey);
      console.warn('[Adtrace] Failed to track purchase revenue', error);
    });
  }, [verifyResult, nativeStoreProvider, resolvedPlanId, resolvedPaymentType, data?.plan_name, planData, appliedDiscountCode]);

  const handleCheckoutClick = () => {
    if (!data || !resolvedPlanId || !resolvedPaymentType) return;

    void analyticsService.logEvent('begin_checkout', {
      plan_id: String(resolvedPlanId),
      plan_name: data.plan_name,
      payment_type: resolvedPaymentType,
      value: Number(activePriceSummary?.totalAmount || data.discount_price || data.price || 0),
      currency: 'IRR',
      ...(analyticsSource
        ? {
          source: analyticsSource,
          notification_campaign: notificationCampaign || 'unknown',
        }
        : {}),
      ...(resolvedPaymentType === 'gems' && data.gems_amount
        ? { gems_amount: data.gems_amount }
        : {}),
    });

    void createAndRedirect();
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = (params.get('status') || '').toLowerCase();
    const trackId = params.get('track_id') || '';
    if (!status && !trackId) return;

    const doVerify = async () => {
      try {
        if (inFlightVerify.current) return;
        inFlightVerify.current = true;
        setIsVerifying(true);
        setVerifyDone(false);
        verifyStartAt.current = Date.now();

        const res = await paymentServiceApi.post<any>('/payments/verify', { track_id: trackId });

        const payload = res.data || {};

        // ApiClient.success means the HTTP request succeeded. The payment
        // result is the backend response body's `success` field.
        if (!res.success || payload.success !== true) {
          if (
            status === 'ok' &&
            (res.success === false || payload.status === 'pending') &&
            verifyRetryCount.current < MAX_VERIFY_RETRIES
          ) {
            verifyRetryCount.current += 1;
            inFlightVerify.current = false;
            setTimeout(() => doVerify(), VERIFY_RETRY_DELAY_MS);
            return;
          }

          setVerifyResult({
            success: false,
            code: payload.code ?? null,
            message: payload.message || res.error || 'خطا در وریفای پرداخت',
            order_id: payload.order_id ?? null,
            status: payload.status ?? 'failed',
            plan_name: payload.plan_name ?? null,
            amount: payload.amount ?? null,
          });
          const elapsed = Date.now() - verifyStartAt.current;
          const remain = Math.max(0, MIN_SPINNER_MS - elapsed);
          if (remain > 0) setTimeout(() => setVerifyDone(true), remain);
          else setVerifyDone(true);
          return;
        }

        setVerifyResult({
          success: payload.success === true,
          code: payload.code ?? null,
          message: payload.message ?? null,
          order_id: payload.order_id ?? null,
          status: payload.status ?? null,
          plan_name: payload.plan_name ?? null,
          amount: payload.amount ?? null,
        });

        const elapsed = Date.now() - verifyStartAt.current;
        const remain = Math.max(0, MIN_SPINNER_MS - elapsed);
        if (remain > 0) setTimeout(() => setVerifyDone(true), remain);
        else setVerifyDone(true);

        try {
          sessionStorage.removeItem('payment_order_id');
          sessionStorage.removeItem('payment_plan_id');
        } catch {}
      } catch (e: unknown) {
        if (
          (new URLSearchParams(location.search).get('status') || '').toLowerCase() === 'ok' &&
          verifyRetryCount.current < MAX_VERIFY_RETRIES
        ) {
          verifyRetryCount.current += 1;
          inFlightVerify.current = false;
          setTimeout(() => doVerify(), VERIFY_RETRY_DELAY_MS);
          return;
        }

        setVerifyResult({ success: false, message: e instanceof Error ? e.message : 'بروز خطا در وریفای' });
        const elapsed = Date.now() - verifyStartAt.current;
        const remain = Math.max(0, MIN_SPINNER_MS - elapsed);
        if (remain > 0) setTimeout(() => setVerifyDone(true), remain);
        else setVerifyDone(true);
      } finally {
        setIsVerifying(false);
        inFlightVerify.current = false;
      }
    };

    doVerify();
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const inCallback = !!(params.get('status') || params.get('track_id'));

    if (verifyResult && !inCallback && !isNativeStoreBuild()) {
      navigate(verifyResult.success ? '/library' : '/premium-subscription', { replace: true });
    }
  }, [verifyResult, location.search, navigate]);

  const qs = new URLSearchParams(location.search);
  const inCallback = !!(qs.get('status') || qs.get('track_id'));
  const activePriceSummary = priceSummary ?? buildDefaultPriceSummary(data);

  if (!resolvedPlanId && !autoSelectSubscriptionPlan && !inCallback) {
    return (
      <div className="min-h-screen bg-gray-50 font-persian flex flex-col items-center justify-center p-4 text-center" dir="rtl">
        <h2 className="text-xl font-bold text-red-600 mb-4">خطا در پردازش اطلاعات پرداخت</h2>
        <p className="text-gray-600 mb-6">شناسه پلن نامعتبر است. لطفاً دوباره از صفحه مربوطه اقدام کنید.</p>
        <button
          onClick={handleGoBack}
          className="bg-primary-500 text-white font-bold py-3 px-6 rounded-lg hover:bg-primary-600 transition-colors"
        >
          بازگشت
        </button>
      </div>
    );
  }

  return (
    <div className={cn('min-h-screen bg-gray-50 font-persian relative', className)}>
      <div className="bg-white px-4 py-3 flex items-center justify-between sticky top-0 z-50 border-b border-gray-100 pt-safe-top">
        <div className="flex items-center justify-between w-full mt-2">
        <button
          onClick={() => navigate(-1)}
          className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="بازگشت"
        >
          <ChevronRight className="w-5 h-5 text-gray-700" />
        </button>
        <h1 className="text-base font-bold text-gray-900">
          {resolvedPaymentType === 'gems' ? 'خرید الماس' : 'خرید اشتراک'}
        </h1>
        <div className="w-8" />
        </div>
      </div>

      <main className="p-4 max-w-md mx-auto pt-4 pb-32">
        {(isVerifying || verifyResult || inCallback) && (
          <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
            <div className="flex justify-center mb-6">
              <div
                className={cn(
                  'w-20 h-20 rounded-full flex items-center justify-center',
                  (isVerifying || (!verifyResult?.success && !verifyDone) || (inCallback && !verifyResult))
                    ? 'bg-blue-100'
                    : verifyResult?.success
                      ? 'bg-green-100'
                      : 'bg-red-100'
                )}
              >
                {(isVerifying || (!verifyResult?.success && !verifyDone) || (inCallback && !verifyResult)) ? (
                  <svg className="w-10 h-10 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : verifyResult?.success ? (
                  <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
            </div>

            {isVerifying || (!verifyResult?.success && !verifyDone) || (inCallback && !verifyResult) ? (
              <div className="text-center">
                <div className="flex items-center justify-center space-x-2 mb-4">
                  <svg className="animate-spin h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span className="text-lg font-semibold text-gray-800">در حال دریافت اطلاعات...</span>
                </div>
                <p className="text-gray-600">لطفاً صبر کنید</p>
              </div>
            ) : (
              <div className="text-center space-y-4">
                <h3 className={cn('text-2xl font-bold mb-4', verifyResult?.success ? 'text-green-700' : 'text-red-700')}>
                  {verifyResult?.success ? 'پرداخت موفق' : 'پرداخت ناموفق'}
                </h3>

                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  {verifyResult?.order_id && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">شماره سفارش:</span>
                      <span className="font-mono text-gray-800">{verifyResult.order_id}</span>
                    </div>
                  )}
                  {verifyResult?.code && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">کد پیگیری خطا:</span>
                      <span className="font-mono text-gray-800">{verifyResult.code}</span>
                    </div>
                  )}
                  {verifyResult?.plan_name && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">نام پلن:</span>
                      <span className="font-semibold text-gray-800">{toPersianNumber(verifyResult.plan_name)}</span>
                    </div>
                  )}
                  {verifyResult?.amount && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">مبلغ:</span>
                      <span className="font-semibold text-gray-800">{Number(verifyResult.amount).toLocaleString('fa-IR')} تومان</span>
                    </div>
                  )}
                </div>

                <p className="text-gray-700 text-center">
                  {verifyResult?.message || (verifyResult?.success ? 'پرداخت شما با موفقیت انجام شد' : 'متاسفانه پرداخت شما موفق نبود')}
                </p>

                <div className="pt-4">
                  <button
                    onClick={() => navigate(verifyResult?.success ? '/library' : '/profile')}
                    className={cn(
                      'w-full text-white font-bold py-4 px-6 rounded-xl transition-colors duration-200',
                      verifyResult?.success ? 'bg-green-600 hover:bg-green-700 shadow-lg' : 'bg-gray-600 hover:bg-gray-700'
                    )}
                  >
                    {verifyResult?.success ? 'شروع مطالعه' : 'بازگشت'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {!isVerifying && !verifyResult && !inCallback && (
          <div className="space-y-6">
            {data && (
              <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
                <h3 className="text-lg font-bold text-gray-800 mb-4 text-center">
                  {resolvedPaymentType === 'gems' ? 'جزئیات خرید الماس' : 'جزئیات اشتراک'}
                </h3>

                <div className="space-y-2">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-[15px] text-gray-600">{resolvedPaymentType === 'gems' ? 'نام بسته:' : 'نوع اشتراک:'}</span>
                    <span className="text-[15px] font-semibold text-gray-800">{toPersianNumber(data.plan_name)}</span>
                  </div>

                  {resolvedPaymentType === 'gems' && planData?.gems_amount ? (
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-[15px] text-gray-600">تعداد الماس:</span>
                      <span className="text-[15px] font-semibold text-gray-800">{planData.gems_amount.toLocaleString('fa-IR')}</span>
                    </div>
                  ) : resolvedPaymentType !== 'gems' && data.duration_value > 0 ? (
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-[15px] text-gray-600">مدت زمان:</span>
                      <span className="text-[15px] font-semibold text-gray-800">
                        {toPersianNumber(data.duration_value)} {data.duration_unit === 'month' ? 'ماه' : data.duration_unit === 'year' ? 'سال' : 'روز'}
                      </span>
                    </div>
                  ) : null}

                  <div className="space-y-1 pt-1">
                    <div className="flex justify-between items-center py-1.5">
                      <span className="text-[15px] text-gray-600">قیمت اصلی:</span>
                      <span className={data.discount_price ? 'text-[15px] text-gray-500 line-through' : 'text-[15px] font-semibold text-gray-800'}>
                        {formatPrice(data.price)}
                      </span>
                    </div>

                    {data.discount_price && (
                      <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
                        <span className="text-[15px] text-gray-600">قیمت پلن:</span>
                        <span className="text-[15px] font-semibold text-gray-800">{formatPrice(data.discount_price)}</span>
                      </div>
                    )}

                    {activePriceSummary && activePriceSummary.discountPercent > 0 && (
                      <div className="flex justify-between items-center py-1.5">
                        <span className="text-[15px] text-gray-600">مبلغ تخفیف:</span>
                        <span className="text-[15px] font-semibold text-green-700">
                          {formatPrice(activePriceSummary.discountAmount)}
                        </span>
                      </div>
                    )}

                    {activePriceSummary && (
                      <>
                        <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
                          <span className="text-[15px] text-gray-600">ارزش افزوده:</span>
                          <span className="text-[15px] font-semibold text-gray-800">{formatPrice(activePriceSummary.vatAmount)}</span>
                        </div>
                        <div className="flex justify-between items-center py-2">
                          <span className="text-[15px] text-gray-600">مبلغ قابل پرداخت:</span>
                          <span className="text-[15px] font-bold text-primary-600">{formatPrice(activePriceSummary.totalAmount)}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
              <div className="mb-5 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="mb-2 flex items-center gap-2 text-gray-800">
                  <Percent className="h-4 w-4 text-primary-600" />
                  <span className="text-sm font-bold">کد تخفیف</span>
                </div>

                <div className="flex items-stretch gap-2 max-[420px]:flex-col">
                  <div className="relative min-w-0 flex-[0.8] max-[420px]:flex-1">
                    <input
                      type="text"
                      value={discountCodeInput}
                      onChange={(e) => handleDiscountCodeInputChange(e.target.value)}
                      placeholder="کد تخفیف رو وارد کن"
                      className="!mb-0 !h-11 !py-0 !pr-4 !pl-16 w-full rounded-xl border border-gray-200 bg-gray-50 text-right text-sm font-semibold tracking-normal text-gray-800 outline-none transition-colors focus:border-primary-400 focus:bg-white"
                      dir="rtl"
                    />
                    {!discountCodeInput.trim() && (
                      <button
                        type="button"
                        onClick={handlePasteDiscountCode}
                        className="absolute left-2 top-1/2 flex h-6 -translate-y-1/2 items-center justify-center rounded-md bg-white px-2.5 py-0 text-[11px] font-bold leading-none text-primary-600 shadow-sm ring-1 ring-gray-200 transition-colors hover:bg-primary-50"
                      >
                        الصاق
                      </button>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={handleApplyDiscount}
                    disabled={isApplyingDiscount || !discountCodeInput.trim()}
                    className={cn(
                      'h-11 shrink-0 rounded-xl px-4 text-sm font-bold transition-all max-[420px]:w-full',
                      isApplyingDiscount || !discountCodeInput.trim()
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-900 text-white hover:bg-black active:scale-95'
                    )}
                  >
                    {isApplyingDiscount ? 'در حال بررسی...' : 'اعمال'}
                  </button>
                </div>

                {discountFeedback && (
                  <div
                    className={cn(
                      'mt-3 flex items-start gap-2 rounded-xl px-3 py-2 text-sm',
                      discountFeedback.type === 'success'
                        ? 'border border-green-100 bg-green-50 text-green-700'
                        : 'border border-red-100 bg-red-50 text-red-700'
                    )}
                  >
                    {discountFeedback.type === 'success' ? (
                      <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    ) : (
                      <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    )}
                    <span>{discountFeedback.message}</span>
                  </div>
                )}
              </div>

              <div className="fixed bottom-0 left-0 right-0 z-50 px-4 pt-2 bg-gradient-to-t from-white/85 via-white/65 to-transparent backdrop-blur-md" style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}>
                <div className="w-full max-w-md mx-auto">
                  <button
                    disabled={isCreating || !data}
                    onClick={handleCheckoutClick}
                    className={cn(
                      'w-full font-bold py-4 px-6 rounded-xl transition-all duration-200 shadow-lg',
                      isCreating || !data
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-primary-600 hover:bg-primary-700 text-white hover:shadow-xl transform hover:-translate-y-1'
                    )}
                  >
                    {isCreating ? (
                      <div className="flex items-center justify-center space-x-2">
                        <svg className="animate-spin h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span>
                          {isNativeStoreBuild()
                            ? (nativePurchaseStarted ? `در حال انجام پرداخت در ${getStoreDisplayName(nativeStoreProvider)}...` : `در حال بازکردن پرداخت ${getStoreDisplayName(nativeStoreProvider)}...`)
                            : 'در حال انتقال به درگاه پرداخت...'}
                        </span>
                      </div>
                    ) : resolvedPaymentType === 'gems' ? (
                      'پرداخت و دریافت الماس'
                    ) : (
                      'پرداخت و فعال‌سازی اشتراک'
                    )}
                  </button>
                </div>
              </div>

              <div className="mt-4 text-center bg-amber-50 border border-amber-200 rounded-xl p-3">
                <p className="text-sm text-amber-700">
                  لطفاً برای به مشکل نخوردن هنگام پرداخت، فیلترشکن را خاموش کنید
                </p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default PaymentConfirmation;
